import { useAppDispatch } from '_store';
import { DialogType } from '_store/application/types';
import ivimsReducer from '_store/ivim/ivimsReducers';
import { ivimType } from '_store/ivim/ivimsTypes';
import { ivimsSelector } from '_store/ivim/selectors';
import {
  getIvimTitle,
  mapPictogramText,
  mapServiceCategoryCode,
  mapServiceCategorySubCode,
} from '_store/ivim/utils';
import { useLocalization } from '@fluent/react';
import { CardContent, Grid, Typography } from '@material-ui/core';
import { isFuture } from 'date-fns';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { PictogramFullID } from '../../icons/ivim/PictogramFulID';
import { PictogramImage } from '../../icons/ivim/PictogramImage';
import { images } from '../../icons/ivim/pictograms';
import EventLayout from './EventLayout';
import styles from './styles';

interface IvimLayoutProps {
  id: string;
  onUpdateIvim?: (idExpanded: string) => void;
  onCloneIvim?: (idExpanded: string) => void;
}

const IvimLayout: FC<IvimLayoutProps> = ({ id, onUpdateIvim, onCloneIvim }) => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const { canWriteIvim, blockedActionMode } = useRolePermissions();
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const events = useSelector(ivimsSelector);
  const event = events.find((ivim) => ivim.id === id);

  const onDeleteIvim = () => {
    dispatch(ivimsReducer.actions.deleteIVIM(id));
  };

  const onDisableIvim = () => {
    dispatch(ivimsReducer.actions.disableIVIM({ ids: [id], disable: true }));
  };

  const onEnableIvim = () => {
    dispatch(ivimsReducer.actions.disableIVIM({ ids: [id], disable: false }));
  };

  const mapIvimType = (type: number) => {
    const matchedType = ivimType().find((t) => t.value === type);
    if (matchedType) {
      return matchedType.label;
    }
    return '-';
  };

  if (!event) {
    return (
      <EventLayout
        type={DialogType.IVIM}
        active={false}
        header={'Unknown event'}
        disabled={false}
        scheduled={false}
        auditResourceId={id}
      >
        Unknown event #{id} selected
      </EventLayout>
    );
  }

  const header = getIvimTitle(event, l10n);
  // TODO fix commented stuff after IVIM reimplementation
  return (
    <EventLayout
      type={DialogType.IVIM}
      disabled={event.disabled && new Date(event.expiresAt) > new Date()}
      active={isFuture(new Date(event.ends))}
      header={header}
      date={new Date(event.starts)}
      broadcastingFrequency={event.broadcastingFrequency.toString()}
      onDelete={onDeleteIvim}
      onDisable={onDisableIvim}
      onEnable={onEnableIvim}
      onUpdate={() => (onUpdateIvim ? onUpdateIvim(event.id) : null)}
      onClone={() => (onCloneIvim ? onCloneIvim(event.id) : null)}
      scheduled={isFuture(new Date(event.starts))}
      auditResourceId={event.id}
      writeAllowed={canWriteIvim}
      blockedActionMode={blockedActionMode}
      blockedMessage={writeBlockedMessage}
      specialHeader={
        event.metadata.connectedDenms &&
        event.metadata.connectedDenms.length > 0 && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <img
                src={images.speedLimit110}
                style={{
                  width: 'auto',
                  height: '1.5rem',
                  marginRight: '0.5rem',
                  verticalAlign: 'middle',
                }}
              />
              <strong>Max Speed Limit Reduced to 110 due to HeavyRain</strong>
            </Grid>
          </Grid>
        )
      }
    >
      <CardContent className={classes.detailStyle}>
        <Grid container spacing={1} style={{ marginTop: 10 }}>
          <Grid item xs={12} className={classes.gridVertical}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              ID: <strong>{event.id}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-sp-country-code')}{' '}
              <strong>{event?.countryCode ?? '-'}</strong>
            </Typography>

            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-sp-country-id')}{' '}
              <strong>{event?.providerIdentifier ?? '-'}</strong>
            </Typography>

            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-type')} <strong>{mapIvimType(event?.iviType)}</strong>
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-valid-from')} {`\n`}
              <strong>{new Date(event.createdAt).toLocaleString()}</strong>
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-valid-to')} {`\n`}
              <strong>{new Date(event.ends).toLocaleString()}</strong>
            </Typography>
          </Grid>

          <Grid item xs={12} className={classes.gridVertical}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-reference-point')}
              {'\n'}
              <strong>
                {event.referencePoint.coordinates[1]},{event.referencePoint.coordinates[0]}
              </strong>
            </Typography>

            {event.metadata.freeText?.length > 0 &&
              event.metadata.freeText.map((text, index) => (
                <Grid container spacing={1} key={index} style={{ marginBottom: 10 }}>
                  <Grid item xs={6}>
                    <Typography
                      component={'span'}
                      variant={'body2'}
                      paragraph
                      className={classes.textStyle}
                    >
                      {l10n.getString('forms-ivim-text')} {`\n`}
                      <strong>{text.value}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} style={{ paddingLeft: 10 }}>
                    <Typography
                      component={'span'}
                      variant={'body2'}
                      paragraph
                      className={classes.textStyle}
                    >
                      {l10n.getString('forms-ivim-language-text')} {`\n`}
                      <strong>{text.language ?? '-'}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            {event.metadata.extraText?.length > 0 &&
              event.metadata.extraText.map((text, index) => (
                <Grid container spacing={1} style={{ marginBottom: 10 }} key={index}>
                  <Grid item xs={6}>
                    <Typography
                      component={'span'}
                      variant={'body2'}
                      paragraph
                      className={classes.textStyle}
                    >
                      {l10n.getString('forms-ivim-extra-text')} {`\n`}
                      <strong>{text.value}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} style={{ paddingLeft: 10 }}>
                    <Typography
                      component={'span'}
                      variant={'body2'}
                      paragraph
                      className={classes.textStyle}
                    >
                      {l10n.getString('forms-ivim-language-text')} {`\n`}
                      <strong>{text.language ?? '-'}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              ))}

            <PictogramImage
              pictogramNature={event?.pictogramNature}
              pictogramSerialNumber={event?.pictogramSerialNumber}
              serviceSubCategoryCode={event?.serviceSubCategoryCode}
            />

            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-pictogram-code')} {`\n`}
              <strong>
                <PictogramFullID
                  serviceCategoryCode={event?.serviceCategoryCode}
                  serviceSubCategoryCode={event?.serviceSubCategoryCode}
                  pictogramCategoryCode={event?.pictogramNature}
                  pictogramSerialNumber={event?.pictogramSerialNumber}
                />
              </strong>
            </Typography>

            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-ser-cat-cod')} {`\n`}
              <strong>{mapServiceCategoryCode(event?.serviceCategoryCode, l10n)}</strong>
            </Typography>

            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-ser-sub-cat-cod')} {`\n`}
              <strong>{mapServiceCategorySubCode(event?.serviceSubCategoryCode, l10n)}</strong>
            </Typography>

            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-pictogram-name')} {`\n`}
              <strong>
                {mapPictogramText(
                  event?.pictogramNature,
                  event?.pictogramSerialNumber,
                  event?.serviceCategoryCode,
                  event?.serviceSubCategoryCode,
                )}
              </strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-ivim-source')} {`\n`}
              <strong>
                {event.source === 'datexii'
                  ? l10n.getString('forms-event-source-datexii')
                  : event.source}
              </strong>
            </Typography>
            {event.metadata.connectedDenms && event.metadata.connectedDenms.length > 0 && (
              <Typography
                component={'span'}
                variant={'body2'}
                paragraph
                className={classes.textStyle}
              >
                {l10n.getString('forms-ivim-connected-denms')} {`\n`}
                <strong>
                  {event.metadata.connectedDenms.map((denm) => denm.emerasId).join(', ')}
                </strong>
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </EventLayout>
  );
};

export default React.memo(IvimLayout);
